import axios, { AxiosError } from 'axios';
import {
  clearCustomerAuth,
  getCustomerToken,
  type CustomerAuthUser,
} from './customer-auth';

// Same backend, different token namespace. Splitting the axios instance
// from the professional one keeps the two auth contexts strictly
// separated even when both are stored in the same browser.
const BASE_URL =
  (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3002';

export const customerApi = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

customerApi.interceptors.request.use((config) => {
  try {
    const token = getCustomerToken();
    if (token) {
      config.headers = config.headers ?? {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // Silently fail on token retrieval — the endpoint will reject if needed.
    console.error('Failed to get customer token for request:', e);
  }
  return config;
});

// Login may legitimately return 401 on bad credentials — let the form
// surface that itself instead of auto-logging-out.
const FORM_OWNED_401_PATTERNS = ['/login'];

// Background polling endpoints. A 401 here rejects the promise but
// doesn't wipe the session — a flaky poll shouldn't sign a working
// session out, and a genuinely-invalid token will be caught by the
// next foreground call.
const SILENT_401_PATTERNS = [
  '/me/notifications/unread-count',
  '/me/notifications',
];

customerApi.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    try {
      if (err.response?.status === 401) {
        const url = (err.config?.url ?? '').toString();
        const isFormOwned = FORM_OWNED_401_PATTERNS.some((p) => url.includes(p));
        const isSilent = SILENT_401_PATTERNS.some((p) => url.includes(p));
        if (!isFormOwned && !isSilent) {
          try {
            clearCustomerAuth();
          } catch (e) {
            console.error('Failed to clear customer auth:', e);
          }
          if (
            typeof window !== 'undefined' &&
            !window.location.pathname.includes('/signin')
          ) {
            try {
              window.location.href = '/signin';
            } catch (e) {
              console.error('Failed to redirect to signin:', e);
            }
          }
        }
      }
    } catch (e) {
      console.error('Error in customer API response interceptor:', e);
    }
    return Promise.reject(err);
  },
);

// A raw, unlocalised i18n key — a dotted lowercase token with no spaces
// (e.g. "messages.booking.slotTaken"). If the server ever fails to resolve
// a key it returns the key verbatim; we must never show that to a customer.
function looksLikeI18nKey(s: string): boolean {
  // Only suppress ACTUAL leaked backend keys, which always live under the
  // `messages.` namespace (e.g. "messages.booking.slotTaken"). The old
  // pattern matched ANY dotted lowercase token, which silently masked
  // legitimate error text behind the generic fallback — making real
  // failures (like a broken checkout) impossible to diagnose from the UI.
  return /^messages\.[\w.]+$/.test(s.trim());
}

export function customerApiErrorMessage(
  err: unknown,
  fallback = 'Something went wrong.',
): string {
  const e = err as AxiosError<any>;
  const body = e?.response?.data?.message;
  // NestJS ValidationPipe returns `message` as a string[] — surface the
  // first readable entry instead of dropping straight to the fallback.
  const fromBody = Array.isArray(body)
    ? body.find((m) => typeof m === 'string' && m.trim())
    : body;
  const msg =
    (typeof fromBody === 'string' && fromBody.trim() ? fromBody : null) ||
    (e as any)?.message;
  // Surface the server message only when it's real human-readable text —
  // never a leaked i18n key like "messages.booking.slotTaken".
  if (typeof msg === 'string' && msg.trim() && !looksLikeI18nKey(msg)) {
    return msg;
  }
  // We're about to show the generic fallback — log the raw server payload
  // + status so the true cause is still visible in DevTools.
  if (e?.response) {
    // eslint-disable-next-line no-console
    console.warn('[api] error masked behind fallback →', {
      status: e.response.status,
      data: e.response.data,
    });
  }
  return fallback;
}

// ============================================================
// Auth
// ============================================================

export async function checkEmail(email: string) {
  const res = await customerApi.post('/customer-auth/check-email', { email });
  return res.data as { status: boolean; available: boolean };
}

export async function signup(payload: {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  accept_terms: boolean;
  // Address is captured at signup and required on the backend.
  // Latitude / longitude come from the Google Places picker.
  address_line1: string;
  postcode: string;
  latitude: number;
  longitude: number;
  // Optional supplementary fields.
  mobile_number?: string | null;
  dial_code?: string | null;
  address_line2?: string | null;
  city?: string | null;
  country_code?: string | null;
}) {
  const res = await customerApi.post('/customer-auth/signup', payload);
  return res.data as {
    status: boolean;
    message: string;
    accessToken: string;
    user: CustomerAuthUser;
    verification_url?: string;
  };
}

export async function verifyEmail(token: string) {
  const res = await customerApi.post('/customer-auth/verify-email', { token });
  return res.data as
    | {
        status: true;
        alreadyVerified: false;
        accessToken: string;
        message: string;
        user: CustomerAuthUser;
      }
    | {
        status: true;
        alreadyVerified: true;
        accessToken: string;
        message: string;
        user: CustomerAuthUser;
      };
}

export async function resendVerification(email: string) {
  const res = await customerApi.post('/customer-auth/resend-verification', {
    email,
  });
  return res.data as {
    status: boolean;
    message: string;
    verification_url?: string;
  };
}

export async function login(payload: { email: string; password: string }) {
  const res = await customerApi.post('/customer-auth/login', payload);
  return res.data as {
    status: boolean;
    message: string;
    accessToken: string;
    user: CustomerAuthUser;
  };
}

export async function logout() {
  try {
    await customerApi.post('/customer-auth/logout');
  } catch {
    // Server is JWT-only — nothing to invalidate. Swallow error and
    // clear local auth regardless.
  }
}

export async function forgotSendOtp(email: string) {
  const res = await customerApi.post(
    '/customer-auth/forgot-password/send-otp',
    { email },
  );
  return res.data as {
    status: boolean;
    reference_number: string | null;
    message: string;
  };
}

export async function forgotVerifyOtp(payload: {
  reference_number: string;
  email: string;
  otp: string;
}) {
  const res = await customerApi.post(
    '/customer-auth/forgot-password/verify-otp',
    payload,
  );
  return res.data as {
    status: boolean;
    remember_token: string;
    message: string;
  };
}

export async function changeOwnPassword(payload: {
  current_password: string;
  new_password: string;
}) {
  const res = await customerApi.put('/customer-auth/change-password', payload);
  return res.data as { status: boolean; message: string };
}

// Step 1 of authenticated email change — backend re-auths with the
// current password, then mails an OTP to the NEW address. Persist the
// returned `reference_number` for step 2.
export async function changeOwnEmailSendOtp(payload: {
  new_email: string;
  current_password: string;
}) {
  const res = await customerApi.post(
    '/customer-auth/change-email/send-otp',
    payload,
  );
  return res.data as {
    status: boolean;
    reference_number: string;
    message: string;
  };
}

// Step 2 — submit the OTP the customer received at `new_email`. On
// success the backend has swapped the login email and marked it verified.
export async function changeOwnEmailVerify(payload: {
  reference_number: string;
  new_email: string;
  otp: string;
}) {
  const res = await customerApi.post(
    '/customer-auth/change-email/verify',
    payload,
  );
  return res.data as { status: boolean; message: string };
}

export async function forgotResetPassword(payload: {
  remember_token: string;
  new_password: string;
}) {
  const res = await customerApi.put(
    '/customer-auth/forgot-password/reset-password',
    payload,
  );
  return res.data as { status: boolean; message: string };
}

// ============================================================
// Profile
// ============================================================

export async function getMe() {
  const res = await customerApi.get('/customer/me');
  return res.data as { status: boolean; info: any };
}

export async function updateMe(patch: Record<string, any>) {
  const res = await customerApi.put('/customer/me', patch);
  return res.data as { status: boolean; message: string };
}

// ============================================================
// Address book (C1) — a customer's saved service addresses. Backed by
// the /customer/addresses CRUD; exactly one is is_default.
// ============================================================

export type CustomerSavedAddress = {
  id: string;
  label: string | null;
  address_line1: string;
  address_line2: string | null;
  city: string | null;
  postcode: string | null;
  country_code: string | null;
  latitude: number;
  longitude: number;
  is_default: boolean;
};

export type SavedAddressInput = {
  label?: string | null;
  address_line1: string;
  address_line2?: string | null;
  city?: string | null;
  postcode?: string | null;
  country_code?: string | null;
  latitude: number;
  longitude: number;
  is_default?: boolean;
};

export async function listMyAddresses() {
  const res = await customerApi.get('/customer/addresses');
  return res.data as { status: boolean; info: CustomerSavedAddress[] };
}

export async function addMyAddress(payload: SavedAddressInput) {
  const res = await customerApi.post('/customer/addresses', payload);
  return res.data as {
    status: boolean;
    info: CustomerSavedAddress;
    message: string;
  };
}

export async function updateMyAddress(
  id: string,
  payload: Partial<SavedAddressInput>,
) {
  const res = await customerApi.put(`/customer/addresses/${id}`, payload);
  return res.data as {
    status: boolean;
    info: CustomerSavedAddress;
    message: string;
  };
}

export async function deleteMyAddress(id: string) {
  const res = await customerApi.delete(`/customer/addresses/${id}`);
  return res.data as { status: boolean; message: string };
}

export async function setDefaultMyAddress(id: string) {
  const res = await customerApi.put(`/customer/addresses/${id}/default`);
  return res.data as { status: boolean; message: string };
}

/** GDPR data export — a JSON copy of the customer's own data. */
export async function exportMyData() {
  const res = await customerApi.get('/customer/me/data-export');
  return res.data as { status: boolean; info: any; generated_at: string };
}

/**
 * GDPR right to erasure — permanently deletes + anonymises the
 * customer's account. Irreversible.
 */
export async function deleteMyAccount() {
  const res = await customerApi.delete('/customer/me');
  return res.data as { status: boolean; message: string };
}

/**
 * Upload a file from a signed-in customer. Bytes go website → API → S3
 * via the shared `/uploads/me` multipart endpoint (UserAuthGuard
 * accepts both customer + pro JWTs). Returns the resulting S3 URL.
 */
export async function uploadFile(
  file: File,
  purpose: 'ADMIN_AVATAR' | 'GENERIC' = 'ADMIN_AVATAR',
) {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('purpose', purpose);
  const res = await customerApi.post('/uploads/me', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as { status: boolean; key: string; url: string };
}

/**
 * Upload a reference photo for the booking wizard's "Your expectations
 * concerning the pro" step. Uses the ANONYMOUS
 * /uploads/booking-attachment route so it works for a logged-out customer
 * mid-flow (signup happens later). Returns the public S3 URL, which the
 * wizard keeps in the booking draft and attaches to the booking at
 * checkout. Image-only + size-capped server-side.
 */
export async function uploadBookingAttachment(file: File) {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await customerApi.post('/uploads/booking-attachment', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as { status: boolean; key: string; url: string };
}

// Presigned-read URLs for already-uploaded reference photos. The bucket
// is private, so the canonical upload URL won't render in an <img> on a
// remount (when the in-memory blob preview is gone). Maps each canonical
// URL → a short-lived signed URL the thumbnail can actually load.
export async function getBookingAttachmentPreviews(urls: string[]) {
  if (!urls.length) return {} as Record<string, string>;
  const res = await customerApi.post(
    '/uploads/booking-attachment/preview-urls',
    { urls },
  );
  return (res.data?.previews ?? {}) as Record<string, string>;
}

// ============================================================
// Platform membership (Cherry Picked Essential / Plus plans)
// ============================================================

export type MembershipBenefits = {
  user_subscription_id: string;
  subscription_id: string;
  plan_name: string;
  discount_percent: number;
  monthly_loyalty_credit: number;
  priority_booking: boolean;
  free_cancellation_hours: number;
  end_date: string | null;
};

export type MembershipPlan = {
  id: string;
  name: string;
  tagline: string | null;
  monthly_price: number;
  yearly_price: number;
  currency: string;
  features: string[] | null;
};

export type MembershipInfo = {
  id: string;
  subscription_type: 'MONTHLY' | 'YEARLY';
  status: 'ACTIVE' | 'CANCELLED' | 'EXPIRED' | 'PENDING';
  start_date: string | null;
  end_date: string | null;
  auto_renew: boolean;
  trans_amount: number;
  currency: string;
  subscription: MembershipPlan | null;
};

export async function getMyMembership() {
  const res = await customerApi.get('/customer/me/subscription');
  return res.data as {
    status: boolean;
    info: MembershipInfo | null;
    benefits: MembershipBenefits | null;
    message: string;
  };
}

export type MembershipPlanOption = MembershipPlan & {
  is_active: boolean;
  benefits?: {
    discount_percent?: number;
    monthly_loyalty_credit?: number;
    priority_booking?: boolean;
    free_cancellation_hours?: number;
  };
};

export async function listMembershipPlans() {
  // /subscription/for-dropdown is intentionally unauthenticated so the
  // landing-page plan grid can use it pre-login. The user_type filter
  // hides pro-only plans from the customer-facing picker.
  const res = await customerApi.get('/subscription/for-dropdown', {
    params: { user_type: 'CUSTOMER' },
  });
  return res.data as {
    status: boolean;
    list: MembershipPlanOption[];
    message: string;
  };
}

export async function startMembershipCheckout(payload: {
  subscription_id: string;
  subscription_type: 'MONTHLY' | 'YEARLY';
  auto_renew?: boolean;
}) {
  const res = await customerApi.post(
    '/customer/me/subscription/checkout',
    payload,
  );
  return res.data as {
    status: boolean;
    user_subscription_id: string;
    checkout_url: string;
    amount: number;
    currency: string;
  };
}

export async function cancelMyMembership(reason?: string) {
  const res = await customerApi.put('/customer/me/subscription/cancel', {
    reason: reason?.trim() || null,
  });
  return res.data as { status: boolean; message: string };
}

// ============================================================
// Vouchers (customer-side)
// ============================================================

export type CustomerVoucherStatus = 'AVAILABLE' | 'USED' | 'EXPIRED' | 'REVOKED';

export type CustomerVoucherRow = {
  id: string;
  status: CustomerVoucherStatus;
  effective_status: CustomerVoucherStatus;
  assigned_at: string;
  expires_at: string | null;
  redeemed_at: string | null;
  notes: string | null;
  voucher: {
    id: string;
    code: string;
    title: string;
    description: string | null;
    discount_type: 'PERCENT' | 'FIXED';
    discount_value: number;
    currency: string;
    // Catalog validity window from the master voucher. valid_from is the
    // date the code becomes usable; null = usable immediately.
    valid_from: string | null;
    valid_until: string | null;
    terms: string | null;
  } | null;
};

export async function listMyVouchers() {
  const res = await customerApi.get('/customer/me/vouchers');
  return res.data as {
    status: boolean;
    available: CustomerVoucherRow[];
    used: CustomerVoucherRow[];
    expired: CustomerVoucherRow[];
    message: string;
  };
}

// ============================================================
// Notification inbox
// ============================================================

export type InboxNotificationStatus = 'PENDING' | 'SENT' | 'READ' | 'FAILED';

export type InboxNotification = {
  id: string;
  status: InboxNotificationStatus;
  sent_at: string | null;
  read_at: string | null;
  notification: {
    id: string;
    title: string;
    body: string;
    action: string | null;
    action_id: string | null;
    image_url: string | null;
    sent_at: string | null;
  };
};

export async function listMyNotifications(opts?: {
  limit?: number;
  offset?: number;
  unread_only?: boolean;
}) {
  const res = await customerApi.get('/me/notifications', {
    params: {
      limit: opts?.limit ?? 20,
      offset: opts?.offset ?? 0,
      unread_only: opts?.unread_only ? 'true' : undefined,
    },
  });
  return res.data as {
    status: boolean;
    data: InboxNotification[];
    total_count: number;
    unread_count: number;
  };
}

export async function unreadNotificationCount() {
  const res = await customerApi.get('/me/notifications/unread-count');
  return res.data as { status: boolean; unread_count: number };
}

export async function markNotificationRead(id: string) {
  const res = await customerApi.put(`/me/notifications/${id}/read`);
  return res.data as { status: boolean; message: string };
}

export async function markAllNotificationsRead() {
  const res = await customerApi.put('/me/notifications/read-all');
  return res.data as { status: boolean; message: string };
}

export async function deleteNotification(id: string) {
  const res = await customerApi.delete(`/me/notifications/${id}`);
  return res.data as { status: boolean; message: string };
}

// ============================================================
// Active sessions (Security page)
// ============================================================

export type UserSessionRow = {
  id: string;
  device_label: string | null;
  os: string | null;
  browser: string | null;
  is_mobile: boolean;
  ip_address: string | null;
  city: string | null;
  country: string | null;
  last_activity_at: string | null;
  created_at: string;
  is_current: boolean;
};

export async function listMySessions() {
  const res = await customerApi.get('/me/sessions');
  return res.data as {
    status: boolean;
    data: UserSessionRow[];
  };
}

export async function revokeSession(id: string) {
  const res = await customerApi.delete(`/me/sessions/${id}`);
  return res.data as { status: boolean; message: string };
}

export async function revokeAllOtherSessions() {
  const res = await customerApi.delete('/me/sessions/others');
  return res.data as { status: boolean; message: string };
}

// ============================================================
// Wallet (gift-card-funded balance + history)
// ============================================================

export type WalletCardSummary = {
  id: string;
  code: string;
  amount_initial: number;
  balance: number;
  currency: string;
  expires_at: string | null;
  buyer_first_name: string | null;
  buyer_last_name: string | null;
  source: 'PURCHASE' | 'ADMIN_ISSUED';
  message: string | null;
};

export type WalletTransactionType =
  | 'CREDIT_GIFT_CARD'
  | 'DEBIT_BOOKING'
  | 'REFUND_BOOKING'
  | 'EXPIRY'
  | 'ADMIN_ADJUSTMENT';

export type WalletTransactionRow = {
  id: string;
  type: WalletTransactionType;
  amount: number; // signed: credits positive, debits negative
  currency: string;
  balance_after: number;
  description: string | null;
  booking_id: string | null;
  gift_card: { id: string; code: string } | null;
  created_at: string;
};

export async function getMyWallet() {
  const res = await customerApi.get('/customer/me/wallet');
  return res.data as {
    status: boolean;
    balance: number;
    currency: string;
    cards: WalletCardSummary[];
  };
}

export async function listMyWalletTransactions(opts?: {
  limit?: number;
  offset?: number;
  direction?: 'credit' | 'debit';
}) {
  const res = await customerApi.get('/customer/me/wallet/transactions', {
    params: {
      limit: opts?.limit ?? 20,
      offset: opts?.offset ?? 0,
      ...(opts?.direction ? { direction: opts.direction } : {}),
    },
  });
  return res.data as {
    status: boolean;
    data: WalletTransactionRow[];
    total_count: number;
  };
}
